/**
 * The one document preview, shared by the upload takeover and the detail pane.
 *
 * Both surfaces render a PDF from a blob URL, but they used to do it with their
 * own markup, their own iframe parameters, and their own fallbacks — so the same
 * file looked different depending on which screen you opened it from, and the
 * detail pane's fallback was a mocked-up page rather than a statement that there
 * was nothing to show. This component owns all of it.
 *
 * It is purely presentational: the caller resolves the bytes (a `File` object URL
 * in the takeover, `useVaultDocumentPreview` on the detail route) and passes a
 * URL in. That keeps it usable from stories and tests with no Apollo.
 *
 * The URL must be renderable in place — never a raw `vaultDocumentDownloadUrl`,
 * which is signed with `Content-Disposition: attachment` and would make the
 * iframe download the file instead of displaying it.
 */
import React from "react";
import { FileIcon } from "@jsq/ds/core";
import * as styles from "./DocumentPreview.css";

/**
 * Only PDFs render inline. Everything else is honestly reported as
 * un-previewable rather than shown behind a placeholder that implies otherwise.
 */
export const canPreviewExtension = (extension?: string): boolean =>
  (extension ?? "").toLowerCase() === "pdf";

/**
 * `toolbar=0&navpanes=0` because both surfaces supply their own chrome (the
 * takeover's file rail, the detail pane's header with download/collapse), so the
 * viewer's built-in bar is duplicate furniture. `view=FitH` opens at page width
 * instead of whatever zoom the viewer defaults to.
 */
const VIEWER_PARAMS = "#toolbar=0&navpanes=0&view=FitH";

export type DocumentPreviewProps = {
  /** File extension, e.g. "pdf" — drives the icon and previewability. */
  extension: string;
  /** Document name, used for the iframe's accessible title. */
  name: string;
  /** Renderable (blob) URL for the bytes. Absent until resolved, or on failure. */
  url?: string;
  /** Bytes are in flight. */
  loading?: boolean;
  /** Why the preview isn't showing. Rendered instead of a blank pane. */
  error?: string;
  /**
   * Trailing copy for the un-previewable state, letting each surface say what
   * happens next ("the document will still be filed…" vs "use the download
   * button above").
   */
  hint?: string;
  /** Optional action (e.g. a Download button) shown in the fallback. */
  action?: React.ReactNode;
};

export function DocumentPreview({
  extension,
  name,
  url,
  loading = false,
  error,
  hint,
  action,
}: DocumentPreviewProps) {
  const ext = (extension || "doc").toLowerCase();

  if (canPreviewExtension(ext) && url) {
    return (
      <iframe
        className={styles.frame}
        src={`${url}${VIEWER_PARAMS}`}
        title={`Preview of ${name}`}
      />
    );
  }

  // A PDF with no bytes is either failed or a fixture that was never uploaded —
  // both get the same honest statement, never a fabricated page.
  const heading = loading ? "Loading preview…" : "Preview not available";

  // An un-renderable file type is a settled fact, so it keeps the long-standing
  // "download instead" copy. A previewable file with no bytes is a failure state,
  // where the reason is more useful than generic advice.
  const detail = error
    ? [error, hint].filter(Boolean).join(" ")
    : canPreviewExtension(ext)
      ? undefined
      : [
          "Please download to take a look.",
          `${ext ? `.${ext} files` : "These files"} can’t be shown here.`,
          hint,
        ]
          .filter(Boolean)
          .join(" ");

  return (
    <div className={styles.fallback}>
      <FileIcon extension={ext} size="large" />
      <div className={styles.title}>{heading}</div>
      {!loading && detail && <div className={styles.sub}>{detail}</div>}
      {!loading && action}
    </div>
  );
}
